import { useForm } from "react-hook-form"
import { useMutation } from "@tanstack/react-query"
import { useNavigate, Link } from "react-router"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Field,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { authProvider } from "@/providers/auth.provider"

interface ResetPasswordPayload {
  password: string
}

export function ResetPasswordForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { register, handleSubmit } = useForm<ResetPasswordPayload>()
  const navigate = useNavigate()

  const { mutate, isPending, error } = useMutation({
    mutationFn: (data: ResetPasswordPayload) =>
      authProvider.resetPassword(data.password),
    onSuccess: () => {
      navigate("/auth")
    },
  })

  const errorMessage = (() => {
    if (!error) return null
    if (error.message === "401") {
      return "This link has expired, please request a new one"
    }
    return "Something went wrong, please try again or request a new reset link"
  })()

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle>Set a new password</CardTitle>
          <CardDescription>Enter your new password below</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit((data) => mutate(data))}>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="password">New password</FieldLabel>
                <Input
                  id="password"
                  type="password"
                  {...register("password", { required: true })}
                />
              </Field>
              {errorMessage && (
                <p className="text-sm text-destructive">{errorMessage}</p>
              )}
              {error?.message === "401" && (
                <p className="text-sm">
                  <Link
                    to="/auth/forgot-password"
                    className="underline-offset-4 hover:underline"
                  >
                    Request a new reset link
                  </Link>
                </p>
              )}
              <Field>
                <Button type="submit" disabled={isPending}>
                  {isPending ? "Saving…" : "Set new password"}
                </Button>
              </Field>
            </FieldGroup>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
